// Véhicules : un objet posé dont l'élément est un « véhicule » (wagon de métro, bateau…) transporte le joueur qui
// monte à bord. Le joueur marche sur une case d'embarquement de l'objet, choisit sa destination (ou annule), et le
// voyage commence : les calques « au départ » se jouent (une porte qui se ferme), puis l'objet suit son chemin (tracé
// comme celui d'un objet mobile) avec le joueur dessus ; au bout de la durée choisie, le cercle noir se referme et le
// joueur arrive sur la case de la destination choisie. Le trajet ne se joue que chez ce joueur : pour les autres, le
// véhicule reste à quai.
import { coveredCells, type MapElement, type MapObject } from './map-elements.ts';
import { objectPose, type ObjectMotion, type ObjectPose } from './object-motion.ts';
import type { Position } from './protocol.ts';

/** Destination proposée au joueur : son nom (le choix affiché), la carte et la case d'arrivée. */
export interface TripDestination {
  name: string;
  toMap: number;
  toX: number;
  toY: number;
  /** Quête à avoir terminée pour que la destination soit proposée ; null : toujours proposée. */
  questId: number | null;
}

/**
 * Trajet : une liste de destinations nommée, partagée par plusieurs véhicules (les rames d'une même ligne de métro). Un
 * véhicule qui suit un trajet propose ses destinations, sauf celles de la carte où il se trouve (le joueur y est déjà).
 */
export interface VehicleRoute {
  id: number;
  name: string;
  destinations: TripDestination[];
}

export type VehicleRouteInput = Omit<VehicleRoute, 'id'>;

/** Trajet tel que listé dans l'administration : avec le nombre de véhicules posés (enregistrés) qui le suivent. */
export interface VehicleRouteEntry extends VehicleRoute {
  uses: number;
}

export interface ObjectTrip {
  /**
   * Question posée en montant à bord, suivie des destinations et d'« Annuler » ; vide avec une seule destination : le
   * voyage part sans rien demander.
   */
  prompt: string;
  /** Temps entre le départ (la destination choisie) et la téléportation, en millisecondes. */
  durationMs: number;
  /** Trajet suivi (ses destinations remplacent celles du véhicule) ; null : les destinations propres au véhicule. */
  routeId: number | null;
  /** Destinations propres au véhicule (vide quand il suit un trajet). */
  destinations: TripDestination[];
}

export const TRIP_LIMITS = { minDurationMs: 500, maxDurationMs: 60_000, maxPromptLength: 200, maxDestinations: 8, maxNameLength: 40, maxRouteNameLength: 60 } as const;
export const DEFAULT_TRIP_PROMPT = 'Où aller ?';
/** Dernier choix, après les destinations : le joueur descend. */
export const TRIP_CANCEL = 'Annuler';

export const newDestination = (toMap: number): TripDestination => ({ name: 'Destination', toMap, toX: 0, toY: 0, questId: null });
export const defaultTrip = (toMap: number): ObjectTrip => ({ prompt: DEFAULT_TRIP_PROMPT, durationMs: 4000, routeId: null, destinations: [newDestination(toMap)] });

/**
 * Destinations que le véhicule posé sur la carte `mapId` peut proposer : celles de son trajet, moins celles de cette
 * carte, ou ses destinations propres (toutes) ; un trajet introuvable (supprimé) n'en propose aucune.
 */
export function tripDestinations(trip: ObjectTrip, mapId: number, route: VehicleRoute | undefined): TripDestination[] {
  if (trip.routeId === null) return trip.destinations;
  return route?.destinations.filter((destination) => destination.toMap !== mapId) ?? [];
}

/** La case (x, y) de la carte est-elle une case d'embarquement de cet objet posé (rotation comprise) ? */
export function isBoardingCell(object: Pick<MapObject, 'x' | 'y' | 'turns'>, element: Pick<MapElement, 'width' | 'height' | 'boardCells'>, { x, y }: Position): boolean {
  for (const covered of coveredCells(object, element)) {
    if (covered.x === x && covered.y === y) return element.boardCells[covered.cell] === true;
  }
  return false;
}

/**
 * Où est le véhicule posé en `start`, `elapsed` ms après le début de son trajet : il va d'étape en étape, en marquant la
 * pause du chemin à chacune, puis reste sur la dernière.
 */
export function tripPose(start: Position, motion: ObjectMotion, elapsed: number): ObjectPose {
  // En boucle, le dernier trajet ramène au départ : le voyage s'arrête juste avant.
  const route = { ...motion, mode: 'loop' as const };
  let end = 0;
  let from = start;
  for (const point of motion.points) {
    end += (Math.hypot(point.x - from.x, point.y - from.y) / motion.speed) * 1000 + motion.pauseMs;
    from = point;
  }
  return objectPose(start, route, Math.max(0, Math.min(elapsed, end - 1e-6)));
}
